import {
  CollapseButton,
  ConfirmDialog,
  intlShape,
  LoadingSpinner,
  utils,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import {isEmpty} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {defineMessages, injectIntl} from 'react-intl';

import stylesAutoLayout from './AutoLayout.less';
import {I18N as AutoLayoutBoxI18N} from './AutoLayoutBox';
import DataChanges from './DataChanges';
import LastChangeAuthor from './LastChangeAuthor';
import styles from './SavableComponent.less';
import SaveDeleteButtons from './SaveDeleteButtons';
import ScreenBanner from './ScreenBanner';
import ScreenError from './ScreenError';
import {
  ComplexComponent,
  ModifiedAsterisk,
  EditButton,
} from 'src/js/containers/auto_layout/components';
import {ContentComponent} from 'src/js/model/auto_layout_content_schema';
import {LayoutComponent} from 'src/js/model/auto_layout_layout_schema';
import {MetadataSchema} from 'src/js/model/auto_layout_metadata_schema';
import {KEYWORD_DATA_CONTENT} from 'src/js/model/auto_layout_screen_data_helpers';

const I18N = defineMessages({
  UNSAVED_CHANGES: {
    id: 'autoLayout.wrapper.unSavedChanges',
    defaultMessage: 'You have unsaved changes.',
    description: 'text for auto layout savable component unsaved changes',
  },
  YOUR_CHANGES: {
    id: 'autoLayout.screen.yourChanges',
    defaultMessage: 'Your changes',
    description:
      'title of user changes in confirm dialog for auto layout savable component',
  },
});

class SavableComponent extends React.PureComponent {
  render() {
    const {
      autoLayoutStateId,
      confirmText,
      conflictLastChangeAuthor,
      content,
      dataChanges,
      dataSets,
      errors,
      expanded,
      filteredArrayItemsDataChanges,
      intl,
      isCollapsed,
      isIndexInvalid,
      isSaved,
      layout,
      metadata,
      notEditable,
      onCancelConfirm,
      onCollapse,
      onConfirm,
      onDataChange,
      onDelete,
      onEdit,
      onReload,
      onReset,
      onSave,
      pathToData,
      readOnly,
      rootData,
      screenBannerConfigProps,
      screenId,
      screenProperties,
      showChangesInConfirm,
      showConfirm,
      showConfirmCancel,
      textOnly,
    } = this.props;
    const {isEditing, isInvalid, isLoading, isRemoving, isSaving} =
      screenProperties;
    return (
      <div
        className={classNames(
          'dlLoadingSpinnerContainerOverflowed',
          'dlFlexBoxContainerInContainer',
          {dlLoadingSpinnerParent: isLoading}
        )}
        data-name={'loadingSpinnerParent'}
      >
        <LoadingSpinner
          isVisible={isLoading}
          text={AutoLayoutBoxI18N.LOADING.id}
        />
        {rootData ? (
          <div
            className={classNames(
              styles.dlSavableComponent,
              stylesAutoLayout.dlScreenComponentContainerVertical
            )}
          >
            <ConfirmDialog
              isOpened={showConfirm}
              handleConfirm={onConfirm}
              handleCancel={onCancelConfirm}
              showCancel={showConfirmCancel}
            >
              {confirmText}
              {(showChangesInConfirm &&
                !isEmpty(filteredArrayItemsDataChanges)) ||
              conflictLastChangeAuthor ? (
                <hr className={stylesAutoLayout.dlHRInFlexBoxFix} />
              ) : null}
              {conflictLastChangeAuthor ? (
                <LastChangeAuthor name={conflictLastChangeAuthor} />
              ) : null}
              {showChangesInConfirm &&
              !isEmpty(filteredArrayItemsDataChanges) ? (
                <DataChanges
                  content={content}
                  dataChanges={filteredArrayItemsDataChanges}
                  layout={layout}
                  metadata={metadata}
                  title={I18N.YOUR_CHANGES.id}
                />
              ) : null}
            </ConfirmDialog>
            <ScreenBanner {...screenBannerConfigProps} />
            <EditButton
              isEditing={isEditing}
              onEdit={onEdit}
              showEdit={!isEditing}
            />
            <ComplexComponent
              autoLayoutStateId={autoLayoutStateId}
              metadata={metadata}
              screenId={screenId}
              content={content}
              isCollapsed={isCollapsed}
              layout={layout}
              dataSets={dataSets}
              onDataChange={onDataChange}
              rootData={rootData[KEYWORD_DATA_CONTENT]}
              dataChanges={dataChanges}
              readOnly={readOnly}
              textOnly={textOnly}
              expanded={expanded}
              pathToData={pathToData}
            />
            <ModifiedAsterisk
              isModified={!isSaved || !isEmpty(dataChanges)}
              text={utils.TranslateUtils.translate(I18N.UNSAVED_CHANGES, intl)}
            />
            <ScreenError errors={errors} />
            {isEditing || isInvalid ? (
              <SaveDeleteButtons
                onDelete={onDelete}
                onSave={onSave}
                onReload={onReload}
                onReset={onReset}
                isCollapsed={isCollapsed}
                isDisabled={notEditable || isIndexInvalid}
                isDirty={!isSaved || !isEmpty(dataChanges)}
                isInvalid={isInvalid}
                isRemoving={isRemoving}
                isLoading={isLoading}
                isSaving={isSaving}
              />
            ) : null}
            {!!layout.isCollapsible() && !isEditing && (
              <div className={styles.dlSavableComponentCollapseButtonDiv}>
                <CollapseButton
                  className={classNames(
                    styles.dlSavableComponentCollapseButton,
                    'fa-2x'
                  )}
                  isDisabled={false}
                  isCollapsed={isCollapsed}
                  onClick={onCollapse}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  }
}

SavableComponent.displayName = 'SavableComponent';

SavableComponent.propTypes = {
  autoLayoutStateId: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  conflictLastChangeAuthor: PropTypes.string,
  content: PropTypes.instanceOf(ContentComponent).isRequired,
  dataChanges: PropTypes.objectOf(PropTypes.any),
  dataSets: PropTypes.objectOf(PropTypes.any).isRequired,
  errors: PropTypes.objectOf(PropTypes.any),
  filteredArrayItemsDataChanges: PropTypes.objectOf(PropTypes.any),
  intl: intlShape.isRequired,
  isCollapsed: PropTypes.bool,
  isIndexInvalid: PropTypes.bool,
  isSaved: PropTypes.bool,
  layout: PropTypes.instanceOf(LayoutComponent).isRequired,
  rootData: PropTypes.objectOf(PropTypes.any),
  metadata: PropTypes.instanceOf(MetadataSchema).isRequired,
  pathToData: PropTypes.string,
  notEditable: PropTypes.bool,
  readOnly: PropTypes.bool,
  showChangesInConfirm: PropTypes.bool,
  showConfirm: PropTypes.bool,
  showConfirmCancel: PropTypes.bool,
  textOnly: PropTypes.bool,
  expanded: PropTypes.bool,
  screenBannerConfigProps: PropTypes.objectOf(PropTypes.any),
  screenId: PropTypes.string.isRequired,
  screenProperties: PropTypes.objectOf(PropTypes.any).isRequired,
  onCancelConfirm: PropTypes.func.isRequired,
  onCollapse: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

SavableComponent.defaultProps = {
  confirmText: '',
  conflictLastChangeAuthor: '',
  dataChanges: undefined,
  errors: undefined,
  filteredArrayItemsDataChanges: undefined,
  isCollapsed: false,
  isIndexInvalid: false,
  isSaved: true,
  notEditable: false,
  pathToData: '',
  readOnly: false,
  rootData: undefined,
  screenBannerConfigProps: undefined,
  showChangesInConfirm: false,
  showConfirm: false,
  showConfirmCancel: true,
  textOnly: false,
  expanded: false,
};

export default injectIntl(SavableComponent);
